/**
 * Theme provider component
 * Manages theme state and provides theme context to the application
 */

import React, { createContext, useContext, useEffect, useState } from 'react'
import { ConfigProvider, type ThemeConfig } from 'antd'
import { lightTheme, darkTheme } from './antd-theme'
import { designTokens, type ThemeMode } from '../tokens'
import type { BaseComponentProps } from '@dbs-portal/core-shared'

// Theme context interface
interface ThemeContextValue {
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
  theme: ThemeConfig
  tokens: typeof designTokens
  isDark: boolean
  isLight: boolean
  toggleTheme: () => void
}

// Create theme context
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

// Theme provider props
interface ThemeProviderProps extends BaseComponentProps {
  defaultMode?: ThemeMode
  storageKey?: string
}

// Get system theme preference
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

// Get stored theme or default
const getInitialTheme = (defaultMode: ThemeMode = 'light', storageKey: string): ThemeMode => {
  if (typeof window === 'undefined') return defaultMode
  
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored && ['light', 'dark', 'auto'].includes(stored)) {
      return stored as ThemeMode
    }
  } catch (error) {
    console.warn('Failed to read theme from localStorage:', error)
  }
  
  return defaultMode
}

// Resolve actual theme mode
const resolveThemeMode = (mode: ThemeMode): 'light' | 'dark' => {
  if (mode === 'auto') {
    return getSystemTheme()
  }
  return mode
}

/**
 * Theme provider component
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultMode = 'light',
  storageKey = 'dbs-portal-theme',
  className,
  ...props
}) => {
  const [mode, setModeState] = useState<ThemeMode>(() => 
    getInitialTheme(defaultMode, storageKey)
  )
  
  const [resolvedMode, setResolvedMode] = useState<'light' | 'dark'>(() => 
    resolveThemeMode(mode)
  )

  // Update resolved mode when mode changes or system preference changes
  useEffect(() => {
    const updateResolvedMode = () => {
      setResolvedMode(resolveThemeMode(mode))
    }

    updateResolvedMode()

    // Listen for system theme changes when in auto mode
    if (mode === 'auto' && typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      mediaQuery.addEventListener('change', updateResolvedMode)
      return () => mediaQuery.removeEventListener('change', updateResolvedMode)
    }

    // Return undefined for other cases
    return undefined
  }, [mode])

  // Update document class and localStorage when theme changes
  useEffect(() => {
    if (typeof document !== 'undefined') {
      // Update document class
      document.documentElement.classList.remove('light', 'dark')
      document.documentElement.classList.add(resolvedMode)

      // Update CSS custom properties
      document.documentElement.setAttribute('data-theme', resolvedMode)
    }

    // Store theme preference
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(storageKey, mode)
      } catch (error) {
        console.warn('Failed to save theme to localStorage:', error)
      }
    }
  }, [mode, resolvedMode, storageKey])

  // Set theme mode
  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode)
  }

  // Toggle between light and dark
  const toggleTheme = () => {
    setMode(resolvedMode === 'light' ? 'dark' : 'light')
  }

  // Get current theme config
  const theme = resolvedMode === 'dark' ? darkTheme : lightTheme
  const isDark = resolvedMode === 'dark'
  const isLight = resolvedMode === 'light'

  // Context value
  const contextValue: ThemeContextValue = {
    mode,
    setMode,
    theme,
    tokens: designTokens,
    isDark,
    isLight,
    toggleTheme,
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider theme={theme}>
        <div 
          className={className}
          data-theme={resolvedMode}
          {...props}
        >
          {children}
        </div>
      </ConfigProvider>
    </ThemeContext.Provider>
  )
}

/**
 * Hook to use theme context
 */
export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

/**
 * Hook to get current theme mode
 */
export const useThemeMode = () => {
  const { mode, setMode, isDark, isLight, toggleTheme } = useTheme()
  return { mode, setMode, isDark, isLight, toggleTheme }
}

/**
 * Hook to get design tokens
 */
export const useDesignTokens = () => {
  const { tokens } = useTheme()
  return tokens
}
